import type { SalesLookupClosedRow, SalesLookupDeal, SalesLookupFamilyDetail } from 'apis/sales-lookup';
import { convertPrice, PriceTier, resolveTier, tierUnit, UomLadder } from 'utils/price-units';

// Pricing model for one variant on the detail screen. The headline is the
// MOST RECENT closed price (evidence beats aspiration); the supplier list is
// one row per supplier — their newest closed price, or their deal when they
// never closed — compared in a single tier so BEST means something.

export interface HeadlinePrice {
  kind: 'CLOSED' | 'DEAL';
  price: number; // ex VAT, quoted on `tier`
  tier: PriceTier;
  unit: string;
  supplierId: number;
  supplierName: string | null;
  closedAt: string | null; // CLOSED only
  quantity: number | null; // CLOSED only
  lastModifiedAt: string | null; // DEAL only
  publicPrice: number | null;
  publicPriceUnit: string | null;
}

export interface SupplierPriceEntry {
  supplierId: number;
  supplierName: string | null;
  kind: 'CLOSED' | 'DEAL';
  price: number; // ex VAT, quoted on `tier`
  tier: PriceTier;
  unit: string;
  comparablePrice: number | null; // ex VAT, converted to the comparison tier; null = not comparable
  isBest: boolean;
  closedAt: string | null;
  lastModifiedAt: string | null;
  publicPrice: number | null;
  publicPriceUnit: string | null;
  history: SalesLookupClosedRow[]; // this supplier's closed rows, newest first
}

export interface VariantPricing {
  comparisonTier: PriceTier;
  comparisonUnit: string;
  headline: HeadlinePrice | null;
  rows: SupplierPriceEntry[];
}

const time = (iso: string | null): number => (iso ? new Date(iso).getTime() : NaN);

const newestClosed = (rows: SalesLookupClosedRow[]): SalesLookupClosedRow | null =>
  rows.reduce<SalesLookupClosedRow | null>(
    (best, row) => (!best || time(row.closedAt) > time(best.closedAt) ? row : best),
    null
  );

// Latest closed row the selected restaurant has for this variant — powers the
// "you currently pay" overlay. Null when the restaurant never closed it.
export function latestClosedForRestaurant(
  detail: SalesLookupFamilyDetail,
  variantId: number,
  restaurantId: number
): SalesLookupClosedRow | null {
  return newestClosed(detail.closed.filter((r) => r.variantId === variantId && r.restaurantId === restaurantId));
}

// Discount of a closed price vs the supplier's CURRENT public price, percent
// (negative = cheaper than public). Null without a usable public price.
export function discountPct(closedPrice: number, publicPrice: number | null): number | null {
  if (publicPrice == null || !(publicPrice > 0)) return null;
  return ((closedPrice - publicPrice) / publicPrice) * 100;
}

// The public price is a free-text-unit catalog number; only compare when its
// unit word matches the closed price's tier unit — never convert into it.
export function historyDiscountPct(
  row: SalesLookupClosedRow,
  publicPrice: number | null,
  publicPriceUnit: string | null,
  ladder: UomLadder
): number | null {
  const unit = tierUnit(ladder, row.priceUnit);
  if (!unit || !publicPriceUnit || unit.toLowerCase() !== publicPriceUnit.trim().toLowerCase()) return null;
  return discountPct(row.price, publicPrice);
}

export function buildVariantPricing(detail: SalesLookupFamilyDetail, variantId: number): VariantPricing | null {
  const variant = detail.variants.find((v) => v.id === variantId);
  if (!variant) return null;

  const ladder: UomLadder = variant;
  const supplierName = new Map(detail.suppliers.map((s) => [s.supplierId, s.supplierName]));
  const publicBySupplier = new Map(
    detail.supplierProducts
      .filter((p) => p.variantId === variantId)
      .map((p) => [p.supplierId, { publicPrice: p.publicPrice, publicPriceUnit: p.publicPriceUnit }])
  );

  const closedRows = detail.closed.filter((r) => r.variantId === variantId);
  const activeDeals = detail.deals.filter((d) => d.variantId === variantId && d.isActive);

  // A deal's rung: its STORED dealUnit when recorded (and still defined on the
  // variant), else the desktop matrix convention — the outermost rung the
  // variant defines (packUnit || itemUnit || baseUnit).
  const dealTierOf = (deal?: SalesLookupDeal | null): PriceTier => resolveTier(ladder, deal?.dealUnit) ?? 'BASE';
  const dealTier: PriceTier = dealTierOf(null);

  const latest = newestClosed(closedRows);
  const comparisonTier: PriceTier = latest ? latest.priceUnit : dealTier;
  const comparisonUnit = tierUnit(ladder, comparisonTier) ?? variant.baseUnit;

  const supplierIds = new Set<number>([
    ...closedRows.map((r) => r.supplierId),
    ...activeDeals.map((d) => d.supplierId),
  ]);

  const rows: SupplierPriceEntry[] = [...supplierIds].flatMap((supplierId) => {
    const history = closedRows
      .filter((r) => r.supplierId === supplierId)
      .sort((a, b) => time(b.closedAt) - time(a.closedAt));
    const newest = history[0] ?? null;
    // Newest active deal per supplier (same convention as build-price-matrix).
    const supplierDeal = activeDeals
      .filter((d) => d.supplierId === supplierId)
      .reduce<SalesLookupDeal | null>(
        (best, d) => (!best || time(d.lastModifiedAt) > time(best.lastModifiedAt) ? d : best),
        null
      );

    if (!newest && !supplierDeal) return [];

    const kind: 'CLOSED' | 'DEAL' = newest ? 'CLOSED' : 'DEAL';
    const price = newest ? newest.price : (supplierDeal as SalesLookupDeal).dealPrice;
    const tier = newest ? newest.priceUnit : dealTierOf(supplierDeal);
    const pub = publicBySupplier.get(supplierId);

    return [
      {
        supplierId,
        supplierName: supplierName.get(supplierId) ?? null,
        kind,
        price,
        tier,
        unit: tierUnit(ladder, tier) ?? variant.baseUnit,
        comparablePrice: convertPrice(price, tier, comparisonTier, ladder),
        isBest: false,
        closedAt: newest?.closedAt ?? null,
        lastModifiedAt: supplierDeal?.lastModifiedAt ?? null,
        publicPrice: pub?.publicPrice ?? null,
        publicPriceUnit: pub?.publicPriceUnit ?? null,
        history,
      },
    ];
  });

  // Comparable rows first, cheapest first; incomparable rows trail in name
  // order — visible, but never claiming BEST.
  rows.sort((a, b) => {
    if (a.comparablePrice != null && b.comparablePrice != null) return a.comparablePrice - b.comparablePrice;
    if (a.comparablePrice != null) return -1;
    if (b.comparablePrice != null) return 1;
    return (a.supplierName ?? '').localeCompare(b.supplierName ?? '');
  });

  const bestPrice = rows.find((r) => r.comparablePrice != null)?.comparablePrice;
  for (const row of rows) row.isBest = row.comparablePrice != null && row.comparablePrice === bestPrice;

  let headline: HeadlinePrice | null = null;
  if (latest) {
    const pub = publicBySupplier.get(latest.supplierId);
    headline = {
      kind: 'CLOSED',
      price: latest.price,
      tier: latest.priceUnit,
      unit: tierUnit(ladder, latest.priceUnit) ?? variant.baseUnit,
      supplierId: latest.supplierId,
      supplierName: supplierName.get(latest.supplierId) ?? null,
      closedAt: latest.closedAt,
      quantity: latest.quantity,
      lastModifiedAt: null,
      publicPrice: pub?.publicPrice ?? null,
      publicPriceUnit: pub?.publicPriceUnit ?? null,
    };
  } else if (activeDeals.length) {
    const cheapest = activeDeals.reduce((best, d) => (d.dealPrice < best.dealPrice ? d : best));
    const pub = publicBySupplier.get(cheapest.supplierId);
    const cheapestTier = dealTierOf(cheapest);
    headline = {
      kind: 'DEAL',
      price: cheapest.dealPrice,
      tier: cheapestTier,
      unit: tierUnit(ladder, cheapestTier) ?? variant.baseUnit,
      supplierId: cheapest.supplierId,
      supplierName: supplierName.get(cheapest.supplierId) ?? null,
      closedAt: null,
      quantity: null,
      lastModifiedAt: cheapest.lastModifiedAt,
      publicPrice: pub?.publicPrice ?? null,
      publicPriceUnit: pub?.publicPriceUnit ?? null,
    };
  }

  return { comparisonTier, comparisonUnit, headline, rows };
}
